<template>
  <div class="ur-manual">
    <!-- 工具列（全部靠右：儲存 / 匯入 / 匯出 / 修改紀錄）-->
    <div class="ur-manual-toolbar">
      <button class="ur-manual-tb-save" :disabled="saving" @click="save">
        {{ $t('URBANRENEWAL_ADMIN.MANUAL.SAVE') }}
      </button>
      <button class="ur-manual-tb-excel" @click="triggerImport">
        {{ $t('URBANRENEWAL_ADMIN.MANUAL.IMPORT') }}
      </button>
      <button class="ur-manual-tb-excel" @click="exportData">
        {{ $t('URBANRENEWAL_ADMIN.MANUAL.EXPORT') }}
      </button>
      <button class="ur-manual-tb-history" @click="toggleDrawer">
        {{ $t('URBANRENEWAL_ADMIN.MANUAL.CHANGELOG') }}
      </button>
      <!-- 隱藏 file input -->
      <input
        type="file"
        accept=".xlsx"
        :key="fileInputKey"
        style="display:none"
        ref="fileInput"
        @change="onFileChange"
      />
    </div>

    <!-- 成功提示 -->
    <div v-if="successMsg" class="ur-manual-success">{{ successMsg }}</div>

    <!-- 錯誤提示條 -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- 讀取中 -->
    <div v-if="loading" class="ur-lo-loading">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') || '載入中...' }}</div>

    <!-- 表格 -->
    <div v-else class="ur-manual-wrap">
      <table class="ur-table ur-manual-table">
        <thead>
          <!-- 群組標題列（雙層彩色）-->
          <tr>
            <th rowspan="2" class="ur-manual-grpcol-h">{{ $t('URBANRENEWAL_ADMIN.MANUAL.GRP_COL') }}</th>
            <th :colspan="nOwner" class="ur-manual-grp ur-manual-grp-owner">
              <div class="ur-manual-grp-inner">
                <span>{{ $t('URBANRENEWAL_ADMIN.MANUAL.OWNER') }}</span>
                <button class="ur-manual-grp-add" :disabled="nOwner >= 8" @click="addOwnerCol">+</button>
              </div>
            </th>
            <th :colspan="nHouse" class="ur-manual-grp ur-manual-grp-house">
              <div class="ur-manual-grp-inner">
                <span>{{ $t('URBANRENEWAL_ADMIN.MANUAL.HOUSE') }}</span>
                <button class="ur-manual-grp-add" :disabled="nHouse >= 8" @click="addHouseCol">+</button>
              </div>
            </th>
            <th :colspan="nPark" class="ur-manual-grp ur-manual-grp-park">
              <div class="ur-manual-grp-inner">
                <span>{{ $t('URBANRENEWAL_ADMIN.MANUAL.PARKING') }}</span>
                <button class="ur-manual-grp-add" :disabled="nPark >= 8" @click="addParkCol">+</button>
              </div>
            </th>
          </tr>
          <!-- 子欄列（人01／選房01／選車01）-->
          <tr>
            <th
              v-for="i in ownerCols"
              :key="'oh'+i"
              class="ur-manual-sub ur-manual-sub-owner"
              :class="{ 'is-grpstart': i === 0 }"
            >
              <span class="ur-manual-sub-label">{{ $t('URBANRENEWAL_ADMIN.MANUAL.SUB_OWNER') }}{{ String(i + 1).padStart(2, '0') }}</span>
              <span
                v-if="ownerColEmpty(i) && nOwner > 1"
                class="ur-manual-col-del"
                :title="$t('URBANRENEWAL_ADMIN.MANUAL.DELETE_COL')"
                @click="deleteOwnerCol(i)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              </span>
            </th>
            <th
              v-for="i in houseCols"
              :key="'hh'+i"
              class="ur-manual-sub ur-manual-sub-house"
              :class="{ 'is-grpstart': i === 0 }"
            >
              <span class="ur-manual-sub-label">{{ $t('URBANRENEWAL_ADMIN.MANUAL.SUB_HOUSE') }}{{ String(i + 1).padStart(2, '0') }}</span>
              <span
                v-if="houseColEmpty(i) && nHouse > 1"
                class="ur-manual-col-del"
                :title="$t('URBANRENEWAL_ADMIN.MANUAL.DELETE_COL')"
                @click="deleteHouseCol(i)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              </span>
            </th>
            <th
              v-for="i in parkCols"
              :key="'ph'+i"
              class="ur-manual-sub ur-manual-sub-park"
              :class="{ 'is-grpstart': i === 0 }"
            >
              <span class="ur-manual-sub-label">{{ $t('URBANRENEWAL_ADMIN.MANUAL.SUB_PARK') }}{{ String(i + 1).padStart(2, '0') }}</span>
              <span
                v-if="parkColEmpty(i) && nPark > 1"
                class="ur-manual-col-del"
                :title="$t('URBANRENEWAL_ADMIN.MANUAL.DELETE_COL')"
                @click="deleteParkCol(i)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIdx) in sortedRows" :key="row._key">
            <!-- 組別（列號）-->
            <td class="ur-manual-grpcol">{{ rowIdx + 1 }}</td>
            <!-- 所有權人欄:以 id 選取,徹底避免同名歧義 -->
            <td v-for="i in ownerCols" :key="'oc'+i">
              <UrbanRenewalSearchSelect
                class="ur-manual-owner-ss"
                :value="row.owners[i]"
                :options="ownerSelectOptions"
                @input="$set(row.owners, i, $event)"
              />
            </td>
            <!-- 選配房屋欄 -->
            <td v-for="i in houseCols" :key="'hc'+i" :class="{ 'ur-cell-warn': cellInvalid('house', row.houses[i]) }">
              <input
                class="ur-manual-input"
                v-model="row.houses[i]"
                list="ur-house-list"
                autocomplete="off"
              />
            </td>
            <!-- 選配車位欄 -->
            <td v-for="i in parkCols" :key="'pc'+i" :class="{ 'ur-cell-warn': cellInvalid('park', row.parks[i]) }">
              <input
                class="ur-manual-input"
                v-model="row.parks[i]"
                list="ur-park-list"
                autocomplete="off"
              />
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td :colspan="nOwner + nHouse + nPark + 1" class="ur-td-empty">
              {{ currentProjectId ? $t('URBANRENEWAL_ADMIN.MANUAL.NO_DATA') : $t('URBANRENEWAL_ADMIN.MANUAL.SELECT_PROJECT') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- datalists for autocomplete（房屋/車位用編號,唯一無歧義) -->
    <datalist id="ur-house-list">
      <option v-for="c in houseCodes" :key="c" :value="c"></option>
    </datalist>
    <datalist id="ur-park-list">
      <option v-for="c in parkCodes" :key="c" :value="c"></option>
    </datalist>

    <!-- 新增列（表格下方單一「+」）-->
    <div class="ur-manual-addrow-bar">
      <button class="ur-manual-addrow-btn" :title="$t('URBANRENEWAL_ADMIN.MANUAL.ADD_ROW')" @click="addRow">+</button>
    </div>

    <!-- Changelog Drawer -->
    <div v-if="drawerOpen" class="ur-manual-drawer">
      <div class="ur-manual-drawer-header">
        <span class="ur-manual-drawer-title">{{ $t('URBANRENEWAL_ADMIN.MANUAL.CHANGELOG') }}</span>
        <button class="ur-manual-drawer-close" @click="toggleDrawer">&#10005;</button>
      </div>
      <div class="ur-manual-drawer-body">
        <div v-if="changelog.length === 0" class="ur-drawer-empty">
          {{ $t('URBANRENEWAL_ADMIN.MANUAL.CHANGELOG_EMPTY') }}
        </div>
        <div
          v-for="log in changelog"
          :key="log.id"
          class="ur-log-item"
        >
          <div class="ur-log-header" @click="toggleLog(log.id)">
            <div class="ur-log-header-left">
              <span class="ur-log-date">{{ fmtDate(log.created_on) }}</span>
              <span class="ur-log-author">{{ log.created_by_name }}</span>
            </div>
            <span class="ur-log-chevron" :class="{ 'is-open': expandedLogId === log.id }">&#9660;</span>
          </div>
          <div v-if="expandedLogId === log.id" class="ur-log-detail">
            <div
              v-for="(entry, idx) in (log.changes && log.changes.changes ? log.changes.changes : [])"
              :key="idx"
              class="ur-log-entry"
            >
              【{{ entry.group_name }}】{{ entry.operation }} {{ entry.type }}：{{ entry.display }}
            </div>
            <div v-if="!log.changes || !log.changes.changes || log.changes.changes.length === 0" class="ur-log-entry">—</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 匯入錯誤 dialog -->
    <v-dialog v-model="importErrorDialog.visible" max-width="480">
      <v-card class="ur-dialog-card">
        <div class="ur-dialog-body">
          <p class="ur-dialog-title">
            {{ importErrorDialog.isWarning
              ? $t('URBANRENEWAL_ADMIN.MANUAL.IMPORT_WARN_TITLE')
              : $t('URBANRENEWAL_ADMIN.MANUAL.IMPORT_ERROR_TITLE') }}
          </p>
          <p
            class="ur-dialog-msg"
            :class="{ 'ur-import-error-detail': !importErrorDialog.isWarning }"
            style="white-space: pre-line"
          >{{ importErrorDialog.detail }}</p>
        </div>
        <div class="ur-dialog-actions">
          <button class="ur-dialog-confirm-btn" @click="importErrorDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.MANUAL.CLOSE') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalSearchSelect from '@/components/urbanrenewal/UrbanRenewalSearchSelect.vue';

let _keyCounter = 0;

function nextKey() {
  _keyCounter += 1;
  return _keyCounter;
}

function pad(arr, n) {
  const result = arr.slice(0, n);
  while (result.length < n) result.push('');
  return result;
}

// 由地主清單建立下拉選項:名字唯一→用名字;同名→加門牌;仍重複→加 #id。
// 保證每個標籤唯一,值一律為 id。
function buildOwnerOptions(loData) {
  const nameCount = {};
  loData.forEach((l) => { nameCount[l.display_name] = (nameCount[l.display_name] || 0) + 1; });
  const options = loData.map((l) => {
    let label = l.display_name || `#${l.id}`;
    if (l.display_name && nameCount[l.display_name] > 1) {
      label = l.address ? `${l.display_name}（${l.address}）` : `${l.display_name} #${l.id}`;
    }
    return { id: l.id, label, zone: l.priority_zone || '' };
  });
  // 若標籤仍相撞(同名同門牌),再補 #id 保證唯一
  const labelCount = {};
  options.forEach((o) => { labelCount[o.label] = (labelCount[o.label] || 0) + 1; });
  options.forEach((o) => { if (labelCount[o.label] > 1) o.label = `${o.label} #${o.id}`; });
  return options;
}

export default {
  name: 'UrbanRenewalAdminManualInput',

  components: { UrbanRenewalSearchSelect },

  computed: {
    ...mapState('urbanRenewalAdmin', ['currentProjectId']),

    ownerSelectOptions() {
      return [{ value: '', label: '—' }].concat(
        this.landownerOptions.map((o) => ({ value: o.id, label: o.label }))
      );
    },

    ownerCols() {
      return Array.from({ length: this.nOwner }, (_, i) => i);
    },

    houseCols() {
      return Array.from({ length: this.nHouse }, (_, i) => i);
    },

    parkCols() {
      return Array.from({ length: this.nPark }, (_, i) => i);
    },

    sortedRows() {
      if (this.sortKey === '') return this.rows;
      const copy = this.rows.slice();
      if (this.sortKey === 'name') {
        copy.sort((a, b) => {
          const na = this.ownerLabelById[this.rowFirstOwner(a)] || '';
          const nb = this.ownerLabelById[this.rowFirstOwner(b)] || '';
          return na.localeCompare(nb, 'zh-TW');
        });
      } else if (this.sortKey === 'zone') {
        copy.sort((a, b) => {
          const za = this.zoneMap[this.rowFirstOwner(a)] || '';
          const zb = this.zoneMap[this.rowFirstOwner(b)] || '';
          return za.localeCompare(zb, 'zh-TW');
        });
      }
      return copy;
    },
  },

  data() {
    return {
      rows: [],
      nOwner: 5,
      nHouse: 5,
      nPark: 5,
      landownerOptions: [],   // [{ id, label, zone }] — 地主下拉選項(同名以門牌/編號區分)
      ownerLabelById: {},     // id → 顯示標籤(供排序用)
      houseCodes: [],
      parkCodes: [],
      houseCodeSet: null,
      parkCodeSet: null,
      zoneMap: {},            // id → 優先選區(供依區排序)
      loading: false,
      saving: false,
      errorMsg: '',
      successMsg: '',
      changelog: [],
      drawerOpen: false,
      expandedLogId: null,
      importErrorDialog: { visible: false, detail: '', isWarning: false },
      fileInputKey: 0,
      sortKey: '',
    };
  },

  watch: {
    currentProjectId: {
      immediate: true,
      handler() {
        this.loadAll();
      },
    },
  },

  methods: {
    rowFirstOwner(r) {
      const id = (r.owners || []).find((v) => v !== '' && v != null);
      return id != null ? id : null;
    },

    cellInvalid(type, val) {
      if (!val || !val.trim()) return false;
      const v = val.trim();
      if (type === 'house') return this.houseCodeSet ? !this.houseCodeSet.has(v) : false;
      if (type === 'park') return this.parkCodeSet ? !this.parkCodeSet.has(v) : false;
      return false;
    },

    async loadAll() {
      if (!this.currentProjectId) {
        this.rows = [];
        this.changelog = [];
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      try {
        const [manualRes, loRes, housingRes, parkingRes, clRes] = await Promise.all([
          UrbanRenewalDataService.getManualInput(this.currentProjectId),
          UrbanRenewalDataService.getLandowners(this.currentProjectId),
          UrbanRenewalDataService.getHousingUnits(this.currentProjectId),
          UrbanRenewalDataService.getParkingUnits(this.currentProjectId),
          UrbanRenewalDataService.getChangelog(this.currentProjectId),
        ]);

        // Compute column counts
        const data = manualRes.data;
        this.nOwner = data.length
          ? Math.max(5, ...data.map((g) => (g.landowners ? g.landowners.length : 0)))
          : 5;
        this.nHouse = data.length
          ? Math.max(5, ...data.map((g) => (g.housing ? g.housing.length : 0)))
          : 5;
        this.nPark = data.length
          ? Math.max(5, ...data.map((g) => (g.parking ? g.parking.length : 0)))
          : 5;

        this.rows = data.map((g) => ({
          _key: nextKey(),
          group_id: g.group_id,
          owners: pad((g.landowners || []).map((l) => l.id), this.nOwner),
          houses: pad((g.housing || []).map((h) => h.unit_code), this.nHouse),
          parks: pad((g.parking || []).map((p) => p.unit_code), this.nPark),
        }));

        // Landowner options（值=id,標籤同名可辨識)+ id→標籤 / id→優先選區 對照
        const loData = loRes.data || [];
        this.landownerOptions = buildOwnerOptions(loData);
        const labelById = {};
        const zm = {};
        this.landownerOptions.forEach((o) => {
          labelById[o.id] = o.label;
          zm[o.id] = o.zone;
        });
        this.ownerLabelById = labelById;
        this.zoneMap = zm;

        // Housing lookup sets
        const housingData = housingRes.data || [];
        this.houseCodes = housingData.map((h) => h.unit_code).filter(Boolean);
        this.houseCodeSet = new Set(this.houseCodes);

        // Parking lookup sets
        const parkingData = parkingRes.data || [];
        this.parkCodes = parkingData.map((p) => p.unit_code).filter(Boolean);
        this.parkCodeSet = new Set(this.parkCodes);

        // Changelog
        this.changelog = clRes.data || [];
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.MANUAL.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    addRow() {
      this.rows.push({
        _key: nextKey(),
        group_id: null,
        owners: Array(this.nOwner).fill(''),
        houses: Array(this.nHouse).fill(''),
        parks: Array(this.nPark).fill(''),
      });
    },

    // 新增欄:欄數 +1（上限 8，比照設計稿），並補齊既有列的空格
    addOwnerCol() {
      if (this.nOwner >= 8) return;
      this.nOwner += 1;
      this.rows.forEach((r) => { while (r.owners.length < this.nOwner) r.owners.push(''); });
    },

    addHouseCol() {
      if (this.nHouse >= 8) return;
      this.nHouse += 1;
      this.rows.forEach((r) => { while (r.houses.length < this.nHouse) r.houses.push(''); });
    },

    addParkCol() {
      if (this.nPark >= 8) return;
      this.nPark += 1;
      this.rows.forEach((r) => { while (r.parks.length < this.nPark) r.parks.push(''); });
    },

    // 該欄是否全空（所有列在此欄都無值）→ 空欄才可刪（比照設計稿）
    ownerColEmpty(i) {
      return !this.rows.some((r) => (r.owners[i] != null && r.owners[i] !== ''));
    },

    houseColEmpty(i) {
      return !this.rows.some((r) => (r.houses[i] != null && r.houses[i] !== ''));
    },

    parkColEmpty(i) {
      return !this.rows.some((r) => (r.parks[i] != null && r.parks[i] !== ''));
    },

    // 刪空欄:各群組至少保留 1 欄（比照設計稿）
    deleteOwnerCol(i) {
      if (this.nOwner <= 1) return;
      this.rows.forEach((r) => r.owners.splice(i, 1));
      this.nOwner -= 1;
    },

    deleteHouseCol(i) {
      if (this.nHouse <= 1) return;
      this.rows.forEach((r) => r.houses.splice(i, 1));
      this.nHouse -= 1;
    },

    deleteParkCol(i) {
      if (this.nPark <= 1) return;
      this.rows.forEach((r) => r.parks.splice(i, 1));
      this.nPark -= 1;
    },

    async save() {
      this.saving = true;
      this.errorMsg = '';
      const payload = {
        groups: this.rows.map((r) => ({
          group_id: r.group_id,
          landowners: r.owners.filter((v) => v !== '' && v != null),           // 直接送地主 id
          housing_codes: r.houses.filter((v) => v && v.trim()).map((v) => v.trim()),
          parking_codes: r.parks.filter((v) => v && v.trim()).map((v) => v.trim()),
        })),
      };
      try {
        const res = await UrbanRenewalDataService.saveManualInput(this.currentProjectId, payload);
        const warnings = (res.data && res.data.warnings) || [];
        await this.loadAll();
        this.successMsg = warnings.length > 0
          ? this.$t('URBANRENEWAL_ADMIN.MANUAL.SAVE_WARN', { count: warnings.length })
          : this.$t('URBANRENEWAL_ADMIN.MANUAL.SAVE_SUCCESS');
        setTimeout(() => { this.successMsg = ''; }, 4000);
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.MANUAL.SAVE_FAILED');
      } finally {
        this.saving = false;
      }
    },

    triggerImport() {
      this.$refs.fileInput.click();
    },

    async onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await UrbanRenewalDataService.importManualInput(this.currentProjectId, formData);
        const warnings = (res.data && res.data.warnings) || [];
        await this.loadAll();
        if (warnings.length > 0) {
          // 匯入完成但有項目無法辨識(如同名多筆/編號不存在)→ 明確列出被略過的,不靜默丟棄
          const lines = warnings.map((w) => {
            const gi = (w.group_index != null ? w.group_index : 0) + 1;
            return `• 第 ${gi} 組 ${w.type}「${w.value}」：${w.reason}`;
          });
          this.importErrorDialog = {
            visible: true,
            isWarning: true,
            detail: `${this.$t('URBANRENEWAL_ADMIN.MANUAL.IMPORT_WARN_INTRO')}\n${lines.join('\n')}`,
          };
        } else {
          this.successMsg = this.$t('URBANRENEWAL_ADMIN.MANUAL.SAVE_SUCCESS');
          setTimeout(() => { this.successMsg = ''; }, 2500);
        }
      } catch (err) {
        const detail =
          (err.response && err.response.data && err.response.data.detail) ||
          this.$t('URBANRENEWAL_ADMIN.MANUAL.IMPORT_FAILED');
        this.importErrorDialog = { visible: true, detail, isWarning: false };
      } finally {
        this.fileInputKey += 1;
      }
    },

    async exportData() {
      try {
        const res = await UrbanRenewalDataService.exportManualInput(this.currentProjectId);
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let fn = '手動輸入.xlsx';
        const cd = res.headers['content-disposition'];
        if (cd) {
          const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
          if (m) fn = decodeURIComponent(m[1]);
        }
        a.download = fn;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.MANUAL.EXPORT_FAILED');
      }
    },

    toggleDrawer() {
      this.drawerOpen = !this.drawerOpen;
    },

    toggleLog(id) {
      this.expandedLogId = this.expandedLogId === id ? null : id;
    },

    fmtDate(s) {
      return String(s).replace('T', ' ').slice(0, 16);
    },
  },
};
</script>
